import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useMergeTrips, Trip } from "@/hooks/useTrips";
import { safeFormat } from "@/lib/formatDateTime";

interface MergeTripsDialogProps {
  targetTrip: Trip;
  allTrips: Trip[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MergeTripsDialog({ targetTrip, allTrips, open, onOpenChange }: MergeTripsDialogProps) {
  const [selectedTripIds, setSelectedTripIds] = useState<string[]>([]);
  const mergeTrips = useMergeTrips();

  const otherTrips = allTrips.filter(t => t.id !== targetTrip.id);

  const handleToggleTrip = (tripId: string) => {
    setSelectedTripIds(prev => 
      prev.includes(tripId) 
        ? prev.filter(id => id !== tripId)
        : [...prev, tripId]
    );
  };

  const handleMerge = async () => {
    if (selectedTripIds.length === 0) return;
    
    await mergeTrips.mutateAsync({
      targetTripId: targetTrip.id,
      sourceTripIds: selectedTripIds,
    });
    
    onOpenChange(false);
    setSelectedTripIds([]);
  };

  const formatDateRange = (trip: Trip) => {
    if (!trip.start_date) return "";
    const start = safeFormat(trip.start_date, "MMM d");
    if (!trip.end_date) return start === "—" ? "" : start;
    const end = safeFormat(trip.end_date, "MMM d, yyyy");
    return start === "—" || end === "—" ? "" : `${start} - ${end}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Merge Trips</DialogTitle>
          <DialogDescription>
            Select trips to merge into "{targetTrip.display_name}". The selected trips will be deleted and their items will be added to the target trip.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 mt-4 max-h-64 overflow-y-auto">
          {otherTrips.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No other trips available to merge.
            </p>
          ) : (
            otherTrips.map(trip => (
              <div 
                key={trip.id} 
                className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
              >
                <Checkbox
                  id={trip.id}
                  checked={selectedTripIds.includes(trip.id)}
                  onCheckedChange={() => handleToggleTrip(trip.id)}
                />
                <Label htmlFor={trip.id} className="flex-1 cursor-pointer">
                  <div className="font-medium">{trip.display_name}</div>
                  {trip.destination && (
                    <div className="text-sm text-muted-foreground">{trip.destination}</div>
                  )}
                  {trip.start_date && (
                    <div className="text-xs text-muted-foreground mt-1">
                      {formatDateRange(trip)}
                    </div>
                  )}
                </Label>
              </div>
            ))
          )}
        </div>
        
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleMerge} 
            disabled={mergeTrips.isPending || selectedTripIds.length === 0}
          >
            {mergeTrips.isPending ? "Merging..." : `Merge ${selectedTripIds.length} Trip${selectedTripIds.length !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}